
type StorageType = 'local' | 'session';

/**
 * Get a reference to the desired storage object.
 * @param type - 'local' or 'session'
 */
const getStorage = (type: StorageType): Storage => {
  return type === 'local' ? window.localStorage : window.sessionStorage;
};

/**
 * Save a value to storage.
 * @param key - The storage key under which to save the value.
 * @param value - The value to save (will be serialized as JSON).
 * @param storageType - Which storage to use ('local' or 'session'). Defaults to 'local'.
 */
export function saveToStorage<T>(
  key: string,
  value: T,
  storageType: StorageType = 'local',
): void {
  try {
    const store = getStorage(storageType);
    const serialized = JSON.stringify(value);
    store.setItem(key, serialized);
  } catch (error) {
    console.error(`Error saving "${key}" to ${storageType}Storage:`, error);
  }
}

/**
 * Retrieve a value from storage.
 * @param key - The storage key to retrieve.
 * @param storageType - Which storage to use ('local' or 'session'). Defaults to 'local'.
 * @returns The parsed value, or null if not found or on error.
 */
export function getFromStorage<T>(
  key: string,
  storageType: StorageType = 'local',
): T | null {
  try {
    const store = getStorage(storageType);
    const item = store.getItem(key);
    if (!item) return null;
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Error reading "${key}" from ${storageType}Storage:`, error);
    return null;
  }
}

/**
 * Remove a key from storage.
 * @param key - The storage key to remove.
 * @param storageType - Which storage to use ('local' or 'session'). Defaults to 'local'.
 */
export function removeFromStorage(
  key: string,
  storageType: StorageType = 'local',
): void {
  try {
    const store = getStorage(storageType);
    store.removeItem(key);
  } catch (error) {
    console.error(`Error removing "${key}" from ${storageType}Storage:`, error);
  }
}

/**
 * Clear all entries in the specified storage.
 * @param storageType - Which storage to clear ('local' or 'session'). Defaults to 'local'.
 */
export function clearStorage(storageType: StorageType = 'local'): void {
  try {
    const store = getStorage(storageType);
    store.clear();
  } catch (error) {
    console.error(`Error clearing ${storageType}Storage:`, error);
  }
}

export function clearAuthKeys(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem('auth-storage');
    window.localStorage.removeItem('organization-storage');
    const keys = Object.keys(window.localStorage);
    keys.forEach((key) => {
      if (key.startsWith('sb-')) window.localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Error clearing auth persisted state:', error);
  }
}